import { defineComponent as _defineComponent } from 'vue'

import { Switch } from 'element-ui'
const __default__ = {
  name: 'TransactionSummaryBlock',
  components: {
    [Switch.name]: Switch
  }
}

import { ref, computed, getCurrentInstance, type PropType } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
// Imported by source name (not aliased) so babel-plugin-component pulls
// element-ui/lib/theme-chalk/autocomplete.css. The template uses
// <Autocomplete> directly. Pattern matches CompanyUserPicker.vue.
import { Autocomplete } from 'element-ui'
import { API_LOCATION } from '@/api-config'
import { formatCurrency } from '@/util/currencyFormat'

interface Financials {
  price?: number | string
  total?: number | string
  tax?: number | string
  fee?: number | string
  discount?: number | string
  tips?: number | string
  paid?: number | string
  due?: number | string
  refunded?: number | string
  waive_taxes?: number
  inclusive_tax_waived?: number | string
}

interface CurrentBooking {
  game_name?: string
  group_size?: number
  price?: number | string
}

interface Promo {
  id?: number
  name: string
  value_amount?: number | string
  value_type?: number
  redemption_type?: number
  internal_only?: boolean
  rules?: Array<{ rule_type?: { rule_key?: string } }>
  [key: string]: unknown
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  currentBooking: { type: Object as PropType<CurrentBooking | null>, default: null },
  siblingBookings: { type: Array as PropType<any[]>, default: () => [] },
  lineItems: { type: Array as PropType<any[]>, default: () => [] },
  giftCards: { type: Array as PropType<any[]>, default: () => [] },
  financials: { type: Object as PropType<Financials>, required: true },
  taxLines: { type: Array as PropType<any[]>, default: () => [] },
  feeLines: { type: Array as PropType<any[]>, default: () => [] },
  discountLines: { type: Array as PropType<any[]>, default: () => [] },
  tipLines: { type: Array as PropType<any[]>, default: () => [] },
  transactionId: { type: [String, Number] as PropType<string | number | null>, default: null },
  companyId: { type: [String, Number], required: true },
  companyGroupId: { type: [String, Number], required: true },
  currency: { type: String, default: 'USD' },
  currencySymbol: { type: String, default: '$' },
  currencyFormat: { type: Number, default: 0 },
  numberFormat: { type: Number, default: 0 },
  canUpdate: { type: Boolean, default: false },
  canAddBooking: { type: Boolean, default: false }
},
  emits: ["updated", "add-item", "add-booking"],
  setup(__props, { emit }: { emit: ({
  (e: 'updated'): void
  (e: 'add-item'): void
  (e: 'add-booking'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props





const tipExpanded = ref(false)
const tipAmount = ref<number | null>(null)
const tipAssignee = ref<{ id?: number | string } | null>(null)
const tipSaving = ref(false)
const tipError = ref('')

const promoExpanded = ref(false)
const promoQuery = ref('')
const selectedPromo = ref<Promo | null>(null)
const promoSaving = ref(false)
const promoError = ref('')

// Inclusive tax handling — mirrors TransactionDetails.getSubtotalAmount /
// getInclusiveTaxes (TransactionDetails.vue:10254). DB `transaction.price`
// stores the ex-tax subtotal; the receipt's displayed Subtotal is that plus
// any inclusive tax amounts. Tax rows with pricing_type === 1 are inclusive.
const inclusiveTaxTotal = computed(() => {
  return (props.taxLines || []).reduce((sum, t) => {
    return Number(t?.pricing_type) === 1 ? sum + Number(t?.amount || 0) : sum
  }, 0)
})
// When taxes are waived on an inclusive-tax group the engine still extracts
// the inclusive component from the stored subtotal but records no tax link;
// the drawer lambda derives that amount so the receipt can add it back.
const inclusiveTaxWaived = computed(() => {
  if (Number(props.financials?.waive_taxes) !== 1) return 0
  return Number(props.financials?.inclusive_tax_waived || 0)
})
const displayedSubtotal = computed(() => {
  return Number(props.financials?.price || 0) + inclusiveTaxTotal.value + inclusiveTaxWaived.value
})

// Whether the per-promo discount breakdown should render. Used to gate the
// two mutually-exclusive discount render paths (per-line v-for vs aggregate
// fallback). Inline `discountLines && discountLines.length > 0` checks in
// the template silently failed when paired with v-else-if across the
// <template>/<div> boundary — a Vue 2 compiler quirk we hit on this
// component specifically.
const hasDiscountLines = computed(() => {
  return Array.isArray(props.discountLines) && props.discountLines.length > 0
})

async function getAuthToken() {
  const session = await Auth.currentSession()
  return session.getIdToken().getJwtToken()
}

function getAmount(value: unknown) {
  return formatCurrency(value, {
    currency: props.currency,
    currencySymbol: props.currencySymbol,
    currencyFormat: props.currencyFormat,
    numberFormat: props.numberFormat
  })
}

function formatGiftCardCode(code: string | number | null | undefined) {
  if (!code) return ''
  const str = String(code)
  if (isNaN(Number(str))) return str
  return str.match(/.{1,4}/g)?.join(' ') ?? str
}

// Mirrors TransactionDetails.vue:7998 — buy-x-get-y promos render a badge
// instead of a value-amount line.
function hasBuyXGetYRule(rules: Promo['rules']) {
  if (!rules || rules.length === 0) return false
  return rules.some((r) => r.rule_type && r.rule_type.rule_key === 'BUY_X_GET_Y')
}

function hasMinValueRule(rules: Promo['rules']) {
  if (!rules || rules.length === 0) return false
  return rules.some((r) => r.rule_type && r.rule_type.rule_key === 'MIN_VALUE')
}

function hasBundlePriceRule(rules: Promo['rules']) {
  if (!rules || rules.length === 0) return false
  return rules.some((r) => r.rule_type && r.rule_type.rule_key === 'BUNDLE_PRICE')
}

function requestAddItem() {
  emit('add-item')
}

function cancelTip() {
  tipExpanded.value = false
  tipAmount.value = null
  tipAssignee.value = null
  tipError.value = ''
}

async function submitTip() {
  const amount = parseFloat(String(tipAmount.value ?? ''))
  if (!amount || amount <= 0) {
    tipError.value = 'Enter a valid tip amount'
    return
  }
  if (!props.transactionId) return
  tipSaving.value = true
  tipError.value = ''
  try {
    const token = await getAuthToken()
    const config = { headers: { Authorization: token } }
    const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/transactions/${props.transactionId}/tips`
    const payload: { amount: number; company_user_id?: number | string } = { amount }
    if (tipAssignee.value && tipAssignee.value.id != null) {
      payload.company_user_id = tipAssignee.value.id
    }
    await axios.post(url, payload, config)
    cancelTip()
    emit('updated')
  } catch (err: any) {
    console.error('Failed to add tip:', err)
    tipError.value = (err.response && err.response.data) || 'Failed to add tip'
  } finally {
    tipSaving.value = false
  }
}

function cancelPromo() {
  promoExpanded.value = false
  promoQuery.value = ''
  selectedPromo.value = null
  promoError.value = ''
}

async function promoSearch(queryString: string, cb: (results: Promo[]) => void) {
  try {
    const token = await getAuthToken()
    const config = { headers: { Authorization: token } }
    const today = new Date()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    const purchaseDate = `${today.getFullYear()}-${mm}-${dd}`
    const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/promos?autocomplete=${encodeURIComponent(queryString)}&purchase_date=${purchaseDate}`
    const response = await axios.get(url, config)
    const results: Promo[] = (response.data.promos || [])
      .filter((p: Promo) => p && p.name)
      .map((p: Promo) => ({ ...p, value: p.name }))
    cb(results)
  } catch (err) {
    console.error('Promo search failed:', err)
    cb([])
  }
}

function handlePromoSelect(item: Promo) {
  selectedPromo.value = item
  promoQuery.value = item.name
  promoError.value = ''
}

async function submitPromo() {
  if (!selectedPromo.value) {
    promoError.value = 'Pick a promo from the list'
    return
  }
  if (!props.transactionId) return
  promoSaving.value = true
  promoError.value = ''
  try {
    const token = await getAuthToken()
    const config = { headers: { Authorization: token } }
    const today = new Date()
    const date = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`
    const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/transactions/${props.transactionId}/discounts?date=${date}`
    await axios.post(url, { promo_code: selectedPromo.value }, config)
    cancelPromo()
    emit('updated')
  } catch (err: any) {
    console.error('Failed to apply promo:', err)
    promoError.value = (err.response && err.response.data) || 'Failed to apply promo'
  } finally {
    promoSaving.value = false
  }
}

const waiveSaving = ref(false)
const notifyInstance = getCurrentInstance()
const notify = (opts: any) => notifyInstance?.proxy.$notify(opts)

// Direct-delete handlers for tax/fee/discount/tip rows. No confirm prompt
// (per the chosen UX). On success: toast + emit 'updated' so the drawer
// refetches with the new totals. URL shapes match what TransactionDetails
// uses against the same backend endpoints, so we reuse those lambdas without
// new endpoint work.
//
// Tracking via array (not Set) because Vue 2.7's ref() doesn't observe
// Set/Map .add/.delete mutations — the spinner v-if wouldn't re-render
// after the click. Reassigning .value on each change keeps reactivity.
const deletingLineIds = ref<string[]>([])

function lineKey(prefix: string, item: any) {
  return `${prefix}:${item.id}:${item.link_type || ''}`
}

function markDeleting(key: string) {
  if (!deletingLineIds.value.includes(key)) {
    deletingLineIds.value = [...deletingLineIds.value, key]
  }
}

function clearDeleting(key: string) {
  deletingLineIds.value = deletingLineIds.value.filter(k => k !== key)
}

async function deleteLine(
  endpoint: 'taxes' | 'fees' | 'discounts' | 'tips',
  item: any,
  successLabel: string
) {
  if (!props.transactionId) return
  const key = lineKey(endpoint, item)
  if (deletingLineIds.value.includes(key)) return
  markDeleting(key)
  try {
    const token = await getAuthToken()
    const config = { headers: { Authorization: token } }
    const base = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/transactions/${props.transactionId}`
    let url: string
    if (endpoint === 'tips') {
      url = `${base}/tips?id=${item.id}`
    } else if (endpoint === 'discounts') {
      // delete-discount-from-transaction: only emit link_type when it's not
      // the default 'transaction'. TX-details' deleteDiscount uses the same
      // pattern (TransactionDetails.vue:7804-7810) — sending
      // ?link_type=transaction triggers a wrong branch in the lambda's
      // delete router and the row stays put.
      url = `${base}/discounts?promo_id=${item.id}`
      if (item.link_type && item.link_type !== 'transaction') {
        url += `&link_type=${encodeURIComponent(item.link_type)}`
      }
    } else {
      // taxes / fees share the same param shape — link_type ALWAYS sent
      // (TX-details' deleteTax/deleteFee always include it).
      const typeIdParam = endpoint === 'taxes' ? `tax_id=${item.tax_id}` : `fee_id=${item.fee_id}`
      url = `${base}/${endpoint}?id=${item.id}&${typeIdParam}&item_id=${item.item_id || ''}&link_type=${encodeURIComponent(item.link_type || 'transaction')}`
    }
    await axios.delete(url, config)
    notify({ type: 'success', message: `${successLabel} removed` })
    emit('updated')
    // Keep spinner up briefly so the icon doesn't flash back to trash
    // between DELETE success and the parent's refetch removing the row.
    setTimeout(() => clearDeleting(key), 1200)
  } catch (err: any) {
    console.error(`Failed to remove ${endpoint}:`, err)
    const message = (err.response && err.response.data) || `Failed to remove ${successLabel.toLowerCase()}`
    notify({ type: 'danger', message })
    clearDeleting(key)
  }
}

function deleteTaxLine(t: any) { return deleteLine('taxes', t, 'Tax') }
function deleteFeeLine(f: any) { return deleteLine('fees', f, 'Fee') }
function deleteDiscountLine(d: any) { return deleteLine('discounts', d, 'Discount') }
function deleteTipLine(t: any) { return deleteLine('tips', t, 'Tip') }

function isDeleting(prefix: string, item: any): boolean {
  return deletingLineIds.value.includes(lineKey(prefix, item))
}
async function onWaiveToggle(field: 'waive_taxes' | 'waive_fees', newValue: 0 | 1) {
  if (!props.transactionId || waiveSaving.value) return
  waiveSaving.value = true
  const label = field === 'waive_taxes' ? 'Taxes' : 'Fees'
  try {
    const token = await getAuthToken()
    const config = { headers: { Authorization: token } }
    const url = `${API_LOCATION}companies/${props.companyId}/groups/${props.companyGroupId}/transactions/${props.transactionId}`
    await axios.put(url, { [field]: newValue }, config)
    emit('updated')
    notify({ type: 'success', message: `${label} ${newValue ? 'waived' : 'restored'}` })
  } catch (err: any) {
    console.error('Failed to toggle waive:', err)
    const message = (err.response && err.response.data) || `Failed to update ${label.toLowerCase()}`
    notify({ type: 'danger', message })
  } finally {
    waiveSaving.value = false
  }
}

return { __sfc: true,props, emit, tipExpanded, tipAmount, tipAssignee, tipSaving, tipError, promoExpanded, promoQuery, selectedPromo, promoSaving, promoError, inclusiveTaxTotal, inclusiveTaxWaived, displayedSubtotal, hasDiscountLines, getAuthToken, getAmount, formatGiftCardCode, hasBuyXGetYRule, hasMinValueRule, hasBundlePriceRule, requestAddItem, cancelTip, submitTip, cancelPromo, promoSearch, handlePromoSelect, submitPromo, waiveSaving, notifyInstance, notify, deletingLineIds, lineKey, markDeleting, clearDeleting, deleteLine, deleteTaxLine, deleteFeeLine, deleteDiscountLine, deleteTipLine, isDeleting, onWaiveToggle, Autocomplete }
}

})